import { motion } from 'framer-motion';
import { useState } from 'react';

const PortfolioSection = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const projects = [
    { 
      title: 'RÜYA Turkish Restaurant', 
      subtitle: 'Wine Display, Dubai', 
      url: '/projects/2024 RUYA 1.jpg', 
      category: 'Restaurant' 
    },
    { 
      title: 'EPICURE Culinary School', 
      subtitle: 'Dubai', 
      url: '/projects/2025 EPICURE 1.jpg', 
      category: 'Culinary Institution' 
    },
    { 
      title: 'Les Roches Culinary School', 
      subtitle: 'Abu Dhabi', 
      url: '/projects/2024 CULINARY SCHOOL 1.jpg', 
      category: 'Culinary Institution' 
    },
    { 
      title: 'Bread Street Kitchen', 
      subtitle: 'Pizza Oven, Atlantis Dubai', 
      url: '/projects/2015 PIZZA OVEN 1.jpg', 
      category: 'Fine Dining' 
    },
    { 
      title: 'Le Meridien Dubai', 
      subtitle: 'Fish Tank Display', 
      url: '/projects/2020 FISH TANK 1.jpg', 
      category: 'Hotel' 
    },
    { 
      title: 'Cinepolis Cinema', 
      subtitle: 'Concession Counter', 
      url: '/projects/2023 CC 08.jpg', 
      category: 'Entertainment' 
    },
    { 
      title: 'Nobu', 
      subtitle: 'Atlantis The Palm', 
      url: '/projects/2024 NOBU 1.jpg', 
      category: 'Fine Dining' 
    },
    { 
      title: 'IFZA Wine Chiller', 
      subtitle: 'Dubai', 
      url: "/projects/2023 'IFZA WINE CHILLER 15.jpg", 
      category: 'Wine Storage' 
    },
  ];

  return (
    <section
      id="portfolio"
      className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-24 bg-[#F5F1EB]"
      data-testid="portfolio-section"
    >
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <p className="text-xs uppercase tracking-[0.3em] font-medium text-[#2C6E63] mb-4" data-testid="portfolio-overline">
            Portfolio
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-[#2D3436] mb-6" data-testid="portfolio-heading">
            Featured Projects
          </h2>
          <motion.div 
            initial={{ width: 0 }}
            whileInView={{ width: 64 }}
            transition={{ duration: 1, delay: 0.3 }}
            viewport={{ once: true }}
            className="h-px bg-[#D4C4A8] mx-auto"
          ></motion.div>
        </motion.div>

        {/* Portfolio Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.7, delay: index * 0.1, ease: [0.215, 0.61, 0.355, 1] }}
              viewport={{ once: true, margin: "-50px" }}
              className={`relative overflow-hidden cursor-pointer group ${
                index === 0 ? 'sm:col-span-2 aspect-[2/1]' : 'aspect-square'
              }`}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              data-testid={`portfolio-project-${index}`}
            >
              <motion.img
                src={project.url}
                alt={project.title}
                className="w-full h-full object-cover"
                whileHover={{ scale: 1.08 }}
                transition={{ duration: 1, ease: [0.215, 0.61, 0.355, 1] }}
              />
              
              {/* Default Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#2D3436]/85 via-[#2D3436]/20 to-transparent" />
              
              {/* Hover Overlay */}
              <div
                className={`absolute inset-0 bg-[#2D3436]/75 transition-opacity duration-500 ${
                  hoveredIndex === index ? 'opacity-100' : 'opacity-0'
                }`}
              />

              {/* Content */}
              <div className="absolute inset-0 flex flex-col justify-end p-8">
                <motion.div
                  className="transform"
                  animate={hoveredIndex === index ? { y: -8 } : { y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <p className="text-[#D4C4A8] text-xs uppercase tracking-[0.2em] mb-2">
                    {project.category}
                  </p>
                  <h3 
                    className="font-heading text-2xl md:text-3xl text-white mb-1"
                    data-testid={`portfolio-title-${index}`}
                  >
                    {project.title}
                  </h3>
                  <p className="text-white/70 text-sm font-light">{project.subtitle}</p>
                  
                  {/* Animated divider */}
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={hoveredIndex === index ? { width: 48 } : { width: 0 }}
                    transition={{ duration: 0.5 }}
                    className="h-px bg-[#D4C4A8] mt-4"
                  />
                </motion.div>
              </div>

              {/* Corner Accents */}
              <motion.div 
                className="absolute top-6 left-6 border-t border-l border-white/40"
                initial={{ width: 0, height: 0, opacity: 0 }}
                animate={hoveredIndex === index ? { width: 48, height: 48, opacity: 1 } : { width: 0, height: 0, opacity: 0 }}
                transition={{ duration: 0.5 }}
              />
              <motion.div 
                className="absolute bottom-6 right-6 border-b border-r border-white/40"
                initial={{ width: 0, height: 0, opacity: 0 }}
                animate={hoveredIndex === index ? { width: 48, height: 48, opacity: 1 } : { width: 0, height: 0, opacity: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PortfolioSection;
